import { Grid } from '@mui/material';
import { isEmpty, keys } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import {
  acceptWorkAssignment,
  getWorkAssignmentByCurrentRecruiterAndJob,
  IJob,
} from 'wisestep-api';
import { IWorkAssignment, WsDialog, WsDialogSize } from 'wisestep-base';

import { success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import JobAssignmentSelection from '../../../conversationWidget/components/JobAssignmentSelection';
import ModalForRedirection from '../../../emailSettings/components/twoWayEmailSync/ModalForRedirection';
import { ImportHistoryDrawer, UploadFromSpreadSheets } from '../uploadFromSpreadSheets';

interface IImportBulkSubmissionsProps {
  openBulkUpload: boolean;
  job: IJob;
  onClose(): void;
}

const ImportBulkSubmissions = ({ openBulkUpload, job, onClose }: IImportBulkSubmissionsProps) => {
  const [openJobAssignmentSelection, setOpenJobAssignmentSelection] = useState<boolean>(false);
  const [assignmentData, setAssignmentData] = useState<IWorkAssignment | null>(null);
  const [isAccepted, setIsAccepted] = useState<boolean>(false);
  const [assignmentToTeams, setAssignmentToTeams] = useState<string[]>([]);
  const [assignmentByRecruiters, setAssignmentByRecruiters] = useState<string[]>([]);

  const [openHistoryDrawer, setOpenHistoryDrawer] = useState(false);

  const [showModal, setShowModal] = useState<boolean>(false);

  const setNotification = useNotificationStore((state) => state.setNotification);

  const acceptJobAssignment = useCallback(
    async (assignment: IWorkAssignment, teamIdOrRecruiterId: string | undefined) => {
      try {
        if (teamIdOrRecruiterId) {
          await acceptWorkAssignment(assignment.id, teamIdOrRecruiterId);
          assignment.isAccepted = true;
          setIsAccepted(true);
          setAssignmentData(assignment);
          setNotification(success('Assignment accepted successfully. You can start working now.'));
          return true;
        }
        return false;
      } catch (e) {
        console.log(e);
        return false;
      }
    },
    [setNotification]
  );

  const validAssignment = useCallback(
    async (job: IJob) => {
      if (job.id) {
        const api = await getWorkAssignmentByCurrentRecruiterAndJob(job.id);
        const assignment: IWorkAssignment = api.data;
        if (!isEmpty(api.data)) {
          setAssignmentData(assignment);
          if (!assignment.isClosed) {
            if (!assignment.isAccepted) {
              const assignmentToTeams = keys(assignment.assignedToTeams);
              setAssignmentToTeams(assignmentToTeams);
              const assignmentByRecruiters = keys(assignment.assignedByRecruiters);
              setAssignmentByRecruiters(assignmentByRecruiters);
              const totalAssignees = assignmentToTeams.length + assignmentByRecruiters.length;
              if (totalAssignees > 1) {
                setOpenJobAssignmentSelection(true);
              } else if (totalAssignees == 1) {
                return assignment?.team?.id
                  ? acceptJobAssignment(assignment, assignment.team?.id)
                  : acceptJobAssignment(assignment, assignmentByRecruiters[0]);
              } else {
                const teamIdOrRecruiterId = !isEmpty(assignmentToTeams)
                  ? assignmentToTeams[0]
                  : assignmentByRecruiters[0];
                return acceptJobAssignment(assignment, teamIdOrRecruiterId);
              }
            } else {
              return true;
            }
          } else {
            const jobOwner = job.team ? job.team.name : 'Job Owner';
            setNotification(
              warning(
                `${jobOwner} didn't assign this job to your team.  To add candidates, please get this assigned to your team`
              )
            );
            onClose();
            return false;
          }
        } else {
          const jobOwner = job.team ? job.team.name : 'Job Owner';
          setNotification(
            warning(
              `${jobOwner} didn't assign this job to your team.  To add candidates, please get this assigned to your team`
            )
          );
          onClose();
          return false;
        }
      }
    },
    [acceptJobAssignment, onClose, setNotification]
  );

  useEffect(() => {
    validAssignment(job);
  }, [job, validAssignment]);

  const handleClose = (open: boolean) => (open ? setShowModal(open) : onClose());

  return (
    <>
      {openJobAssignmentSelection && assignmentData != null && (
        <JobAssignmentSelection
          open={openJobAssignmentSelection}
          assignment={assignmentData}
          assignmentToTeams={assignmentToTeams}
          assignmentByRecruiters={assignmentByRecruiters}
          acceptJobAssignment={acceptJobAssignment}
          onClose={() => setOpenJobAssignmentSelection(false)}
        />
      )}
      {openBulkUpload && (assignmentData?.isAccepted ?? isAccepted) && (
        <WsDialog openDialog={openBulkUpload} size={WsDialogSize.LARGE}>
          <Grid p={2}>
            <UploadFromSpreadSheets uploadingFor="submissions" job={job} onClose={handleClose} />
          </Grid>
        </WsDialog>
      )}
      {showModal && (
        <ModalForRedirection
          modalState={showModal}
          onClose={() => handleClose(false)}
          leftButtonText="Skip"
          rightButtonText="View History"
          rightButtonAction={() => setOpenHistoryDrawer(true)}
          message="It may take few minutes to process the submissions data, we will notify you once the cvs are processed."
        />
      )}
      {openHistoryDrawer && (
        <ImportHistoryDrawer
          openDrawer={openHistoryDrawer}
          onClose={() => setOpenHistoryDrawer(false)}
        />
      )}
    </>
  );
};

export default ImportBulkSubmissions;
